import { tools as toolEnums } from '../enums';
import EllipseLineTool from './EllipseLineTool';

const { FibToolLabelFormat } = toolEnums.FIBONACCI;

/** Tool used to draw Fibonacci Arc on chart object */
export default class FibonacciArcTool extends EllipseLineTool {
	/**
	 * Retrieves default parameters for tool, including details and styles
	 * @return {Object} Object containing default tool parameters
	 * @property {String} id - Tool ID
	 * @property {String} name - Tool name
	 * @property {String} labelFormat - Style used to display tool labels
	 * @property {object} style - Tool styles
	 */
	_getDefaultParams() {
		return {
			id: 'fibArc',
			name: 'Fibonacci Arc',
			labelFormat: FibToolLabelFormat.DECIMAL,
			style: this.core.getStyle('tool.fibArc')
		};
	}

	/**
	 * Draws section of Fibonacci (elliptical) arc
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number[]} center - Array of coordinates for center of tool
	 * @param {Number} xDist - Major axis radius of ellipse arc
	 * @param {Number} yDist - Minor axis radius of ellipse arc
	 * @param {Number} i - Index of threshold array
	 * @param {Boolean} isSmiley - Whether or not arc is concave upward
	 */
	fillHalfEllipse(ctx, center, xDist, yDist, i, isSmiley) {
		const { thresholds } = this.params.style;
		ctx.save();
		ctx.beginPath();
		if (i !== 0) {
			this._getDonutSegmentPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				xDist * thresholds[i - 1],
				yDist * thresholds[i - 1],
				isSmiley
			);
		} else {
			this._getDonutSegmentPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				1,
				1,
				isSmiley
			);
		}
		ctx.fill();
		ctx.restore();
	}

	/**
	 * Returns true if current mouse coordinates are within hitbox of this tool
	 * @param {Number[]} mousePanel - Array containing mouse coordinates
	 * @return {Boolean}
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		if (!coords[0] || !coords[1]) return;

		const { thresholds } = this.params.style;
		const ctx = this.panel.rootToolContext;
		const upSlope = coords[0][1] > coords[1][1];
		const xDist = Math.abs(coords[0][0] - coords[1][0]); // x radius component
		const yDist = Math.abs(coords[0][1] - coords[1][1]); // y radius component
		const center = [coords[1][0], coords[1][1]];

		// Set default styles
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		ctx.strokeStyle = '#CCC';
		ctx.fillStyle = '#CCC';
		ctx.lineWidth = this.params.style.width;

		for (let i = thresholds.length - 1; i >= 0; i--) {
			// Set the section line and fill colors
			ctx.strokeStyle = this.params.style.color[i];
			ctx.fillStyle = this.params.style.background.color[i];

			ctx.beginPath();
			this._getHalfArcPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				upSlope
			);
			ctx.stroke();

			this.fillHalfEllipse(ctx, center, xDist, yDist, i, upSlope);
			ctx.textAlign = 'center';

			// Use specific label color if defined, otherwise use the section color
			ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];

			if (upSlope) {
				ctx.fillText(
					this._getFibFormatValue(thresholds[i]),
					center[0],
					center[1] + yDist * thresholds[i] - 5
				);
			} else {
				ctx.fillText(
					this._getFibFormatValue(thresholds[i]),
					center[0],
					center[1] - yDist * thresholds[i] + 15
				); // to put it inside the rings
			}
		}

		ctx.textAlign = 'left'; // reset the text alignment

		if (this.state.selected || this.state.hover) {
			this.handle[0].render();
			this.handle[1].render();
		}

		this._drawControlLine(ctx, coords);

		this.panel.core.onToolRender(this);
	}
}
